import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  FileText,
  Landmark,
  CheckSquare,
  ClipboardList,
  UserCircle,
  List,
  Megaphone,
  Wallet,
  Briefcase,
  Mail,
  Sparkles,
  Search,
  TrendingUp,
  Headset,
  Building2,
  BarChart3,
  Settings,
  HelpCircle,
  Shield,
  Activity,
  History,
  MapPinned,
  Radar,
  ClipboardCheck,
  Users,
  Send,
  Scale,
  ListChecks,
  BookOpen,
} from 'lucide-react';
import { OPS_GROUPS, OPS_PAGES } from '@/pages/ops/opsNav';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Extra words the command palette matches on. */
  keywords?: string[];
  /** What the page is for, in a sentence. The Help Center's map of Flow is
   *  built from these, so it lists exactly what the sidebar does. */
  description?: string;
}

export interface NavSection {
  /** Stable key — the collapsed/expanded state is persisted under it. */
  id: string;
  title?: string;
  items: NavItem[];
}

/**
 * The primary destinations, in the order the sidebar shows them.
 *
 * Settings, Admin, and What's New live in the account menu so they aren't
 * duplicated here. The Help Center is in the account menu AND the top bar's
 * Help menu, which also offers the guide for the page on screen.
 */
export const NAVIGATION: NavSection[] = [
  {
    id: 'home',
    items: [
      {
        label: 'Dashboard', href: '/', icon: LayoutDashboard, keywords: ['home', 'overview'],
        description: 'Your home page: the pipeline at a glance and what needs attention.',
      },
      {
        label: 'AI Imports', href: '/ai-imports', icon: Sparkles, keywords: ['upload', 'om', 'extract'],
        description: 'Broker emails and files Flow has read and turned into analyzed properties.',
      },
      {
        label: 'Inbound Leads', href: '/pipeline/inbound-leads', icon: Mail, keywords: ['valuation', 'offering memorandum'],
        description: 'MyParksValue.com valuation requests, prospect files you uploaded, and AI import jobs — waiting to be worked.',
      },
    ],
  },
  {
    id: 'pipeline',
    title: 'Pipeline',
    items: [
      {
        label: 'Deals', href: '/deals', icon: Briefcase,
        description: 'Transactions we are working, from first look to closing.',
      },
      {
        label: 'Property Lists', href: '/property-lists', icon: List, keywords: ['properties', 'hit list', 'buy box', 'map'],
        description: 'Every park we know of: filter by buy box, map them, save segments, see the Hit List.',
      },
      {
        label: 'Marketing', href: '/marketing', icon: Megaphone, keywords: ['campaigns', 'outreach'],
        description: 'Campaigns and mailings to owners, and the outreach queue.',
      },
      {
        label: 'Financing', href: '/financing', icon: Landmark, keywords: ['lenders', 'debt', 'loans'],
        description: 'Loans in progress and the lenders we work with.',
      },
      {
        label: 'Funds', href: '/funds', icon: Wallet,
        description: 'Investment vehicles and what they hold.',
      },
      {
        label: 'Offers', href: '/offers', icon: FileText, keywords: ['loi'],
        description: 'LOIs and offers we have made, and where each stands. (Owners asking us for an offer start on Research Ops’ Offer Desk.)',
      },
    ],
  },
  {
    id: 'work',
    title: 'Work & Insights',
    items: [
      {
        label: 'Tasks', href: '/tasks', icon: CheckSquare,
        description: 'Work assigned across the team.',
      },
      {
        label: 'Diligence', href: '/diligence', icon: ClipboardList, keywords: ['checklist'],
        description: 'Diligence progress for the properties in active deals. A park’s own checklist is on its Diligence tab.',
      },
      {
        label: 'Contacts', href: '/contacts', icon: UserCircle, keywords: ['people', 'sources', 'brokers'],
        description: 'Owners, brokers and everyone else we deal with.',
      },
      {
        label: 'Owners', href: '/owners', icon: Building2, keywords: ['operators', 'registry'],
        description: 'Who owns which parks — operators and portfolios, with how to reach them.',
      },
      // "Market Research" matches the page's own PageHeader title, and keeps it
      // distinct from Research Ops below — one is MHC transaction data, the
      // other is the external researcher programme.
      {
        label: 'Market Research', href: '/research', icon: Search, keywords: ['comps', 'transactions'],
        description: 'MHC sales and transactions, plus Development Opportunities and Parcel Watch.',
      },
      // '/ops', not '/research-ops': active matching is a startsWith, so a
      // '/research'-prefixed href would light up Market Research too.
      {
        label: 'Research Ops', href: '/ops', icon: Headset, keywords: ['researchers', 'portal', 'desk', 'chat'],
        description: 'Outside researchers calling owners for us: their messages, the four owner-request desks, review, and the team.',
      },
      {
        label: 'Market Intelligence', href: '/markets', icon: TrendingUp, keywords: ['watchlist', 'msa'],
        description: 'Market data and demographics for the areas we watch.',
      },
    ],
  },
];

/**
 * Whether a sidebar item is the current page. A prefix match, on purpose: the
 * detail and edit routes under a section keep that section lit. Several route
 * choices in App.tsx depend on exactly this rule — see the notes there.
 */
export function isNavItemActive(pathname: string, href: string): boolean {
  return pathname === href || (href !== '/' && pathname.startsWith(href));
}

/** The sidebar item that owns a path, if any — used for the top bar's label. */
export function findActiveNavItem(pathname: string): NavItem | undefined {
  for (const section of NAVIGATION) {
    for (const item of section.items) {
      if (isNavItemActive(pathname, item.href)) return item;
    }
  }
  return undefined;
}

/**
 * Pages that are real destinations but have no sidebar entry — reached today
 * from a header button or the account menu. The command palette lists them so
 * each is one keystroke away; nothing here adds a route.
 */
export const SECONDARY_DESTINATIONS: { group: string; items: NavItem[] }[] = [
  {
    group: 'Research & reporting',
    items: [
      { label: 'Reports', href: '/reports', icon: BarChart3, keywords: ['analytics', 'pipeline report', 'win loss'] },
      { label: 'Offer Funnel Report', href: '/reports/offers', icon: BarChart3 },
      { label: 'Development Opportunities', href: '/research/development', icon: MapPinned, keywords: ['entitlement', 'zoning'] },
      { label: 'Parcel Watch', href: '/research/parcel-watch', icon: Radar, keywords: ['county', 'assessor', 'owner changes'] },
      { label: 'Market Comparison', href: '/markets/compare', icon: Scale },
      { label: 'Market Review Queue', href: '/markets/review', icon: ListChecks },
      { label: 'Owner Evidence Queue', href: '/owners/evidence', icon: ClipboardCheck },
      { label: 'Listing Review Queue', href: '/ai-imports/listing-review', icon: ListChecks },
      { label: 'Skip Trace History', href: '/skip-traces', icon: History },
      { label: 'Activity Feed', href: '/activity', icon: Activity },
    ],
  },
  {
    group: 'Research Ops',
    // Built from the Research Ops map itself (src/pages/ops/opsNav.ts), so the
    // palette finds every tab by the name on its heading ("Letter Desk") AND
    // by the name on its tab ("Letters"), and cannot fall behind a new page.
    // The overview is the sidebar's own Research Ops item.
    items: Object.values(OPS_PAGES)
      .filter((p) => p.id !== 'overview')
      .map((p) => ({
        label: p.title,
        href: p.path,
        icon: p.icon,
        keywords: [p.label, OPS_GROUPS.find((g) => g.id === p.group)?.label ?? '', 'research ops'],
        description: p.description,
      })),
  },
  {
    group: 'Outreach',
    items: [
      { label: 'Outreach Queue', href: '/marketing/outreach', icon: Send },
      { label: 'Contact Triage', href: '/contacts/triage', icon: Users },
      { label: 'Lenders', href: '/financing/lenders', icon: Landmark },
    ],
  },
  {
    group: 'Account',
    items: [
      { label: 'Settings', href: '/settings', icon: Settings, keywords: ['preferences'] },
      { label: 'Profile', href: '/settings/profile', icon: UserCircle },
      { label: "What's New", href: '/whats-new', icon: Sparkles, keywords: ['changelog', 'features'] },
      { label: 'Help Center', href: '/help', icon: HelpCircle, keywords: ['help', 'support', 'guide', 'faq', 'docs', 'where'] },
      { label: 'Glossary', href: '/help/glossary', icon: BookOpen, keywords: ['help', 'terms', 'definitions', 'what does'] },
    ],
  },
];

/** Shown in the palette only to admins (the route guards itself regardless). */
export const ADMIN_DESTINATION: NavItem = {
  label: 'Admin Center',
  href: '/admin',
  icon: Shield,
  keywords: ['users', 'access'],
};
